import React, { useState, useEffect } from "react";
import {useDispatch, useSelector} from "react-redux";
import { Tabs } from "flowbite-react";
import _ from "lodash";

import {useDocumentTitle, useFileDownloader} from "../../hooks";
import getPageBreadcrumb from "../../services/BreadcrumbServices";
import BackendLayout from "../../layouts/BackendLayout";
import {ButtonBack, ButtonDownloadFile, ButtonDPad, Label, PageAlert, PageTitle, RedirectAlert} from "../../components";
import {setPageSettings} from "../../store/actions/pageActions";
import {getSearchInspectionHistoryResultSettings} from "../../api/setting/pageApi";
import {
  SEARCH,
  SEARCH_INSPECTION_HISTORY_CONFIRM,
  SEARCH_INSPECTION_HISTORY_RESULT,
  SEARCH_TREATMENT_HISTORY_DETAIL
} from "../../constants/routes";
import {useSearchInspectionHistoryResult} from "../../hooks/searchs/SearchInspectionHistoryResultHook";
import {ArrayUtils} from "../../utils";
import {useNavigate, useLocation} from 'react-router-dom';

import Table from "../../components/table/Table";
import Pagination from "../../components/Pagination";
import {
  clearPageLoading,
  resetAlert,
  setPageLoading,
  clearButtonProcessing,
  setButtonProcessing,
  setAlertSuccess
} from "../../store/actions";

import MachineNoIcon from "../../assets/icons/search-screen-machine-no-icon.png";
import InspectionNumberIcon from "../../assets/icons/inspection-number-icon.svg";
import TemplateNameIcon from "../../assets/icons/search-screen-template-name-icon.png";
import ImplementDateIcon from "../../assets/icons/search-screen-implement-date-icon.png";
import NetWorkIcon from "../../assets/icons/search-screen-machine-group-icon.png";
import {getSearchBackUrl} from "../../utils/url-utils";
import {API_SEARCH_INSPECTION_EXPORT} from "../../constants/api";
import {
  getInspectionResutlColor,
  getInspectionResutlSymbol,
  getInspectionRowBackground
} from "../../utils/display-utils";
import tenkenTheme from "../../configs/themes/tenkenTheme";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import {SEARCH_INSPECTION_HISTORY_RESULT_PAGE} from "../../constants/pageName";
import useRedirectPage from "../../hooks/useRedirectPage";


function SearchInspectionHistoryResult() {
  useRedirectPage(SEARCH_INSPECTION_HISTORY_RESULT_PAGE);
  useDocumentTitle("点検履歴検索結果 - 点検王");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumb = getPageBreadcrumb(SEARCH_INSPECTION_HISTORY_RESULT);
  const groupSettings = useSelector((state) => state.app_settings.machine_group);
  const [searchParams, setSearchParams] = useState(location.state || {});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const {downloadFileFromPath} = useFileDownloader();
  const inspectionResultSymbol = useSelector((s) => s.app_settings.inspection_display.inspection_result_symbol);
  const [workflowId, setWorkflowId] = useState(null);

  useEffect(() => {
    dispatch(resetAlert());
    dispatch(setPageLoading());
    getSearchInspectionHistoryResultSettings().then((pageSettings) => {
      dispatch(setPageSettings(pageSettings));
      dispatch(clearPageLoading());
    });
    const paramUrl = window.location.search;
    if (!_.isEmpty(paramUrl)) {
      const paramSearch = new URLSearchParams(paramUrl);
      const params = {};
      for (const [key, value] of paramSearch.entries()) {
          params[key] = value;
      }
      if (!_.isEmpty(params['page']) && Number.isInteger(Number(params['page']))) {
        setCurrentPage(parseInt(params['page']));
      }
      if (!_.isEmpty(params['per_page']) && Number.isInteger(Number(params['per_page']))) {
        setPageSize(parseInt(params['per_page']));
      }
      if (!_.isEmpty(params['workflow_id']) && Number.isInteger(Number(params['workflow_id']))) {
        setWorkflowId(parseInt(params['workflow_id']));
      }
      setSearchParams(params);
    }
    const successCancelMessage = localStorage.getItem('cancel_message');
    dispatch(setAlertSuccess(successCancelMessage));
    localStorage.removeItem('cancel_message');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const paramUrl = window.location.search;
    const params = {};
    if (!_.isEmpty(paramUrl)) {
      const paramSearch = new URLSearchParams(paramUrl);
      for (const [key, value] of paramSearch.entries()) {
        params[key] = value;
      }
    }
    if (!_.isEmpty(params)) {
      if (_.isEmpty(params['page']) || currentPage !== parseInt(params['page'])) {
        params['page'] = currentPage.toString();
      }
      if (_.isEmpty(params['per_page']) || pageSize !== parseInt(params['per_page'])) {
        params['per_page'] = pageSize.toString();
      }
      if (workflowId !== null) {
        params['workflow_id'] = workflowId;
      }
      if (!_.isEqual(searchParams, params)) {
        setSearchParams(params);
      }
    } else {
      if (currentPage !== parseInt(params['page'])) {
        params['page'] = currentPage.toString();
      }
      if (pageSize !== parseInt(params['per_page'])) {
        params['per_page'] = pageSize.toString();
      }
      if (workflowId !== null) {
        params['workflow_id'] = workflowId;
      }
      if (!_.isEqual(searchParams, params)) {
        setSearchParams(params);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const {
    workflowList,
    workflow,
    searchInformation,
    searchResults,
    totalCount,
    errorMessages
  } = useSearchInspectionHistoryResult(searchParams, workflowId);

  useEffect(() => {
    if (errorMessages.success === false) {
      window.location.href = getSearchBackUrl(searchParams, 'inspection');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessages]);

  const getDateTimeFormat = (dateTime) => {
    if (dateTime === null) return "-";
    const [datePart, timePart] = dateTime.split('T');
    const [year, month, day] = datePart.split('-');
    // eslint-disable-next-line no-unused-vars
    const [hours, minutes, seconds] = timePart.split(':');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  const getUserFormat = (name, userName) => {
    if (name === undefined || name === null) return "-";

    let result = name;
    if (userName) {
      result = result + " (" + userName + ")";
    }
    return result;
  }

  const getTreatmentDisplay = (name) => {
    if (name === undefined || name === null) return "-";
    return <span title={name}>{name}</span>;
  };

  const getJudgmentsDisplay = (row, idx) => {
      if (
        (row[`judgment_result_${idx + 1}`] !== null && idx === 0 && !row[`judgment_result_${idx + 1}`]) ||
        (row[`judgment_result_${idx + 1}`] && row['approve_order'] !== idx)
      ) {
        return getUserFormat(row[`judgment_user_name_${idx + 1}`], row[`judgment_username_${idx + 1}`]);
      }
      return "判定前";
  };

  const getResultIsNumber = (result) => {
    const number = result ? 1 : 0;
    return number;
  };

  function handleBack() {
    window.location.href = SEARCH;
  }

  const urlInspectionHistoryConfirm  = (workflowId, inspectionId) => SEARCH_INSPECTION_HISTORY_CONFIRM
    .replace(':workflowId', workflowId)
    .replace(':inspectionId', inspectionId);
  const handleRedirect = (workflowId, inspectionId) => {
    navigate(urlInspectionHistoryConfirm(workflowId, inspectionId), {state: {searchParams: searchParams, in_url: true}})
  };

  const redirectToTreatmentDetail = (workflowId, treatmentId) => {
    if (treatmentId === undefined || treatmentId === null) return;
    const url = SEARCH_TREATMENT_HISTORY_DETAIL.replace(':treatment_id', treatmentId)
      .replace(':workflow_id', workflowId);
    navigate(url, {state: searchParams})
  }
  const handleActiveTab = (idx) => {
    const selectedTab = workflowList[idx];
    if (selectedTab) {
      setWorkflowId(selectedTab.workflow_id);
      setCurrentPage(1);
      if (workflowId !== selectedTab.workflow_id) {
        setSearchParams({...searchParams, workflow_id: selectedTab.workflow_id})
      }
    }
  }

  const downloadFile = (e, buttonId) => {
    e.preventDefault();
    dispatch(setButtonProcessing(buttonId));
    let urlParams = '';
    for (const key in searchParams) {
      if (urlParams[key] !== null && urlParams[key] !== "") {
        urlParams += `${key}=${searchParams[key]}&`;
      }
    }
    urlParams = urlParams.slice(0, -1);
    downloadFileFromPath(API_SEARCH_INSPECTION_EXPORT(urlParams)).then().finally(() => {
      dispatch(clearButtonProcessing(buttonId));
    });
  }

  return (
    <BackendLayout breadcrumb={breadcrumb} sidebar={true}>
      <PageTitle title="点検履歴検索結果" />
      <RedirectAlert className="!mb-0" />
      <PageAlert className="!mb-0"/>
      <ButtonDPad />
      <div className="py-[4px]">
        <div className="w-fit flex justify-between">
          <div className="flex space-x-5">
            <div className="flex items-center rounded-[8px] bg-white border border-[#E0E0E0] pl-2 xl:pl-4 space-x-4">
              {ArrayUtils.range(1, 5).map((i, idx) =>
                groupSettings[`group_${i}`]?.active && (
                  <div className="flex flex-col" key={idx}>
                    <Label text={groupSettings[`group_${i}`]?.name} className="font-light text-[#616161] py-1 whitespace-nowrap" />
                    <span className="text-left font-medium pb-2 truncate lg:max-w-[240px] max-w-[228px]" title={searchInformation[`group_${i}`]?.name || '[ALL]'}>
                      {searchInformation[`group_${i}`]?.name || '[ALL]'}
                    </span>
                  </div>
                )
              )}
              <div className="min-w-[45px] xl:min-w-[55px] flex justify-end">
                <img src={NetWorkIcon} alt="group-icon" className="w-auto h-[38px] xl:h-[42px] mx-[4px] xl:mx-[12px]" />
              </div>
            </div>
            {searchInformation.machine?.id &&
            <div className="flex items-center rounded-[8px] bg-white border border-[#E0E0E0] pl-2 xl:pl-[16px]">
              <div className="flex flex-col lg:mr-2 mr-1">
                <Label text={groupSettings.machine?.name} className="font-light text-[#616161] py-1 whitespace-nowrap"/>
                <span className="text-left font-medium pb-2 lg:max-w-[240px] max-w-[228px] truncate"
                      title={searchInformation.machine?.name}>{searchInformation.machine?.name || '[ALL]'}</span>
              </div>
              <div className="flex justify-end min-w-[45px] xl:min-w-[55px]">
                <img src={MachineNoIcon} alt="machine-icon"
                     className="w-auto h-[38px] xl:h-[42px] mx-[4px] xl:mx-[12px]"/>
              </div>
            </div>
            }
            {searchInformation.template?.id &&
            <div className="flex items-center rounded-[8px] bg-white border border-[#E0E0E0] pl-2 xl:pl-[16px]">
              <div className="flex flex-col lg:mr-2 mr-1">
                <Label text="点検テンプレート名" className="font-light text-[#616161] py-1 whitespace-nowrap"/>
                <span 
                  className="text-left font-medium pb-2 lg:max-w-[240px] max-w-[228px] truncate"
                  title={searchInformation.template?.name || '[ALL]'}
                >
                  {searchInformation.template?.name || '[ALL]'}
                </span>
              </div>
              <div className="flex justify-end min-w-[45px] xl:min-w-[55px]">
                <img src={TemplateNameIcon} alt="template-icon"
                     className="w-auto h-[38px] xl:h-[42px] mx-[4px] xl:mx-[12px]"/>
              </div>
            </div>
            }
            <div className="flex items-center rounded-[8px] bg-white border border-[#E0E0E0] pl-2 xl:pl-[16px]">
              <div className="flex flex-col min-w-[80px] xl:min-w-[100px] text-[14px]">
                <Label text="点検回数" className="font-light text-[#616161] py-1 whitespace-nowrap"/>
                <span className="text-left font-medium pb-2 whitespace-nowrap">
                  {searchInformation?.inspection_number ? searchInformation.inspection_number + '回目' : '[ALL]'}
                </span>
              </div>
              <div className="flex justify-end min-w-[45px] xl:min-w-[55px]">
                <img src={InspectionNumberIcon} alt="template-icon"
                     className="w-auto h-[38px] xl:h-[42px] mx-[4px] xl:mx-[12px]"/>
              </div>
            </div>
            <div className="flex items-center rounded-[8px] bg-white border border-[#E0E0E0] pl-2 xl:pl-[16px]">
              <div className="flex flex-col w-fit text-[14px]">
                <Label text="検索対象日時" className="font-light text-[#616161] py-1 whitespace-nowrap"/>
                <span className="text-left font-medium pb-2 whitespace-nowrap">{searchInformation.registration_date || '指定なし'}</span>
              </div>
              <div className="flex justify-end min-w-[45px] xl:min-w-[55px]">
                <img src={ImplementDateIcon} alt="datetime-icon"
                     className="w-auto h-[38px] xl:h-[42px] mx-[4px] xl:mx-[12px]"/>
              </div>
            </div>
          </div>

          {searchResults.length > 0 &&
          <div className="flex items-end mx-2 xl:mx-[16px]">
            <ButtonDownloadFile onClick={(e) => downloadFile(e, 'download-inspection-results')} id="download-inspection-results"/>
          </div>
          }
        </div>

        <div className="xl:mt-[16px] mt-2">
          <LoadingSpinner component='search-inspection-results' />
          {searchResults.length > 0 ?
          <>
          <Tabs style="underline" theme={tenkenTheme.search_result_tabs}
                onActiveTabChange={(tab) => handleActiveTab(tab)}>
            {workflowList && workflowList.map((wf, idx) =>
              <Tabs.Item title={wf.workflow_name} key={idx}
                         active={workflowId === wf.workflow_id}
                         onClick={() => setWorkflowId(wf.workflow_id)}>
                <div className="w-fit border border-[#e0e0e0] rounded-[8px] mt-[10px]">
                  <Table striped>
                    <Table.Head className="">
                      <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap w-[200px]">
                        <span className="normal-case">{groupSettings.machine?.name}</span>
                      </Table.HeadCell>
                      {ArrayUtils.range(1, 5).map((i, idx) =>
                        groupSettings[`group_${i}`]?.active && (
                          <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap" key={idx}>
                            <span className="normal-case">{groupSettings[`group_${i}`]?.name}</span>
                          </Table.HeadCell>
                        )
                      )}
                      <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap min-w-[160px">
                        <span>点検結果登録日</span>
                      </Table.HeadCell>
                      <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap text-center min-w-[100px]">
                        <span>点検回数</span>
                      </Table.HeadCell>
                      <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap text-center min-w-[100px]">
                        <span>点検結果</span>
                      </Table.HeadCell>
                      {ArrayUtils.range(1, 10).map((i, idx) =>
                        <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap w-[240px]" key={idx}>
                          <span>処置{i}回目</span>
                        </Table.HeadCell>
                      )}
                      {workflow && workflow.sequences.map((workOrder, key) =>
                          <Table.HeadCell className="px-2 xl:px-[16px] whitespace-nowrap" key={key}>
                    <span className="normal-case">
                      {workOrder.type === 1 ? workOrder.user : workOrder.department}
                    </span>
                          </Table.HeadCell>
                      )}
                    </Table.Head>
                      <Table.Body className="text-[14px] xl:text-base text-gray-900">
                        {searchResults.map((row, index) => (
                          <Table.Row key={index} style={{backgroundColor: getInspectionRowBackground(row.result, inspectionResultSymbol)}}>
                            <Table.Cell className="max-w-[200px] truncate text-[#2374fd] hover:text-[#2374fd] px-2 xl:px-[16px] whitespace-nowrap">
                              <span className="cursor-pointer text-[16px] hover:underline" title={row.machine_name}
                                    onClick={() => handleRedirect(row['workflow_id'], row.id)}>{row.machine_name}</span>
                            </Table.Cell>
                            {ArrayUtils.range(1, 5).map(
                              (i, idx) =>
                                groupSettings[`group_${i}`]?.active && (
                                  <Table.Cell className="max-w-[200px] truncate px-2 xl:px-[16px] whitespace-nowrap" key={idx}>
                                    <span title={row[`machine__group_${i}`] || '指定なし'}>
                                      {row[`machine_group_${i}`] || '指定なし'}
                                    </span>
                                  </Table.Cell>
                                )
                            )}
                            <Table.Cell className="px-2 xl:px-[16px] whitespace-nowrap">
                              {getDateTimeFormat(row.registration_datetime)}
                            </Table.Cell>
                            <Table.Cell className="text-center px-2 xl:px-[16px] whitespace-nowrap">
                              {`${row.inspection_number}回目`}
                            </Table.Cell>
                            <Table.Cell className="text-center px-2 xl:px-[16px] whitespace-nowrap">
                              <span className="p-1"
                                    style={{
                                      color: getInspectionResutlColor(row.result, inspectionResultSymbol),
                                    }}> {getInspectionResutlSymbol(row.result, inspectionResultSymbol)}</span>
                            </Table.Cell>
                            {ArrayUtils.range(1, 10).map((i, idx) =>
                              <Table.Cell className="min-w-[150px] px-2 xl:px-[16px] whitespace-nowrap max-w-[240px] truncate">
                                {(row['treatment_id_' + i] === undefined || row['treatment_id_' + i] === null) ?
                                  getTreatmentDisplay(row['treatment_result_' + i], row['treatment_user_name_' + i])
                                  :
                                  <span
                                    className="text-[#2374fd] cursor-pointer text-[16px] hover:underline hover:text-[#2374fd]"
                                    onClick={() => redirectToTreatmentDetail(row['workflow_id'], row['treatment_id_' + i])}>
                                    <span className="mr-1" 
                                      style={{
                                        color: getInspectionResutlColor(
                                          getResultIsNumber(row['treatment_result_' + i]),
                                          inspectionResultSymbol)
                                      }}
                                    >
                                      {getInspectionResutlSymbol(
                                        getResultIsNumber(row['treatment_result_' + i]),
                                        inspectionResultSymbol
                                      )}
                                    </span>
                                    {getTreatmentDisplay(row['treatment_user_name_' + i])}
                                  </span>
                                }
                              </Table.Cell>
                            )}
                            {workflow && ArrayUtils.range(1, Object.keys(workflow.sequences).length).map(
                              (i, idx) =>
                                <Table.Cell className="min-w-[150px] xl:min-w-[200px] px-2 xl:px-[16px] whitespace-nowrap" key={idx}>
                                  {(
                                    row[`judgment_user_name_${i}`] !== undefined &&
                                    row[`judgment_user_name_${i}`] !== null &&
                                    (
                                      (row[`judgment_result_${idx + 1}`] !== null && idx === 0 && !row[`judgment_result_${idx + 1}`]) ||
                                      (row[`judgment_result_${idx + 1}`] && row['approve_order'] !== idx)
                                    )
                                  ) && (
                                    <span
                                      style={{
                                        color: getInspectionResutlColor(
                                          getResultIsNumber(row[`judgment_result_${i}`]),
                                          inspectionResultSymbol
                                        ),
                                      }}
                                      className="mr-1"
                                    >
                                      {getInspectionResutlSymbol(
                                        getResultIsNumber(row[`judgment_result_${i}`]),
                                        inspectionResultSymbol
                                      )}
                                    </span>
                                  )}
                                  {getJudgmentsDisplay(row, idx)}
                                </Table.Cell>
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                  </Table>
                </div>
              </Tabs.Item>
            )}
          </Tabs>
          <div className="pt-[16px] mr-2 xl:mr-[16px]">
            <Pagination showIndex={totalCount > 0}
                        setPage={setCurrentPage}
                        currentPage={(totalCount !== 0 && currentPage <= Math.ceil(totalCount / pageSize)) ? currentPage : 1}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalCount={totalCount}/>
          </div>
          </>
            : <p>該当する検索結果が見つかりませんでした。別の検索条件をお試しください。</p>
          }
        </div>
        <div className="mt-4 flex justify-center mb-3">
          <ButtonBack onClick={handleBack} />
        </div>
      </div>
    </BackendLayout>
  );
}

export default SearchInspectionHistoryResult